import { supabase } from '@/integrations/supabase/client';
import { ApiResult, ok, err, ApiError } from '../errors';
import type { Json } from '@/integrations/supabase/types';

// ============ Types ============

export type BrandStatus = 'active' | 'trial' | 'suspended' | 'archived';
export type BrandPlanTier = 'starter' | 'professional' | 'enterprise';
export type AdminRole = 'platform_superadmin' | 'brand_admin' | 'group_admin' | 'subgroup_admin';

export interface Brand {
  id: string;
  name: string;
  slug: string;
  industry?: string | null;
  logo_url?: string | null;
  primary_color?: string | null;
  secondary_color?: string | null;
  accent_color?: string | null;
  theme_config?: Json | null;
  custom_domain?: string | null;
  subdomain?: string | null;
  status: BrandStatus;
  plan_tier: BrandPlanTier;
  max_users: number;
  max_products: number;
  settings?: Json | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface BrandAdmin {
  id: string;
  brand_id: string;
  user_id: string;
  role: AdminRole;
  product_ids?: string[] | null;
  permissions?: Json | null;
  invitation_token?: string | null;
  invitation_expires_at?: string | null;
  invited_by?: string | null;
  invitation_email?: string | null;
  accepted_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface BrandWithProducts extends Brand {
  products?: Array<{
    id: string;
    name: string;
    slug: string;
    is_active: boolean;
  }>;
}

export interface CreateBrandInput {
  name: string;
  slug: string;
  industry?: string;
  logo_url?: string;
  primary_color?: string;
  secondary_color?: string;
  accent_color?: string;
  subdomain?: string;
  custom_domain?: string;
  plan_tier?: BrandPlanTier;
  max_users?: number;
  max_products?: number;
  settings?: Json;
}

export interface UpdateBrandInput {
  name?: string;
  slug?: string;
  industry?: string;
  logo_url?: string;
  primary_color?: string;
  secondary_color?: string;
  accent_color?: string;
  theme_config?: Json;
  custom_domain?: string;
  subdomain?: string;
  status?: BrandStatus;
  plan_tier?: BrandPlanTier;
  max_users?: number;
  max_products?: number;
  settings?: Json;
}

// ============ Helper to cast DB row to Brand ============

function castToBrand(row: unknown): Brand {
  const r = row as Record<string, unknown>;
  return {
    id: r.id as string,
    name: r.name as string,
    slug: r.slug as string,
    industry: r.industry as string | null,
    logo_url: r.logo_url as string | null,
    primary_color: r.primary_color as string | null,
    secondary_color: r.secondary_color as string | null,
    accent_color: r.accent_color as string | null,
    theme_config: r.theme_config as Json | null,
    custom_domain: r.custom_domain as string | null,
    subdomain: r.subdomain as string | null,
    status: (r.status as BrandStatus) || 'active',
    plan_tier: (r.plan_tier as BrandPlanTier) || 'starter',
    max_users: (r.max_users as number) || 100,
    max_products: (r.max_products as number) || 3,
    settings: r.settings as Json | null,
    created_at: r.created_at as string | null,
    updated_at: r.updated_at as string | null,
  };
}

function castToBrandAdmin(row: unknown): BrandAdmin {
  const r = row as Record<string, unknown>;
  return {
    id: r.id as string,
    brand_id: r.brand_id as string,
    user_id: r.user_id as string,
    role: r.role as AdminRole,
    product_ids: r.product_ids as string[] | null,
    permissions: r.permissions as Json | null,
    created_at: r.created_at as string | null,
    updated_at: r.updated_at as string | null,
  };
}

// ============ Brand Queries ============

export async function getBrands(): Promise<ApiResult<Brand[]>> {
  const { data, error } = await supabase
    .from('brands')
    .select('*')
    .order('name');

  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }

  return ok((data || []).map(castToBrand));
}

export async function getBrandById(id: string): Promise<ApiResult<Brand | null>> {
  const { data, error } = await supabase
    .from('brands')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }

  return ok(data ? castToBrand(data) : null);
}

export async function getBrandBySlug(slug: string): Promise<ApiResult<Brand | null>> {
  const { data, error } = await supabase
    .from('brands')
    .select('*')
    .eq('slug', slug)
    .maybeSingle();

  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }

  return ok(data ? castToBrand(data) : null);
}

export async function getBrandBySubdomain(subdomain: string): Promise<ApiResult<Brand | null>> {
  // Try subdomain field first, then fall back to slug
  const { data, error } = await supabase
    .from('brands')
    .select('*')
    .or(`subdomain.eq.${subdomain},slug.eq.${subdomain}`)
    .eq('status', 'active')
    .maybeSingle();

  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }

  return ok(data ? castToBrand(data) : null);
}

export async function getBrandByCustomDomain(domain: string): Promise<ApiResult<Brand | null>> {
  const { data, error } = await supabase
    .from('brands')
    .select('*')
    .eq('custom_domain', domain)
    .eq('status', 'active')
    .maybeSingle();

  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }

  return ok(data ? castToBrand(data) : null);
}

export async function getBrandWithProducts(brandId: string): Promise<ApiResult<BrandWithProducts | null>> {
  const { data: brand, error: brandError } = await supabase
    .from('brands')
    .select('*')
    .eq('id', brandId)
    .maybeSingle();

  if (brandError) {
    return err(ApiError.fromSupabaseError(brandError));
  }

  if (!brand) {
    return ok(null);
  }

  const { data: products, error: productsError } = await supabase
    .from('products')
    .select('id, name, slug, is_active')
    .eq('brand_id', brandId)
    .order('name');

  if (productsError) {
    return err(ApiError.fromSupabaseError(productsError));
  }

  return ok({
    ...castToBrand(brand),
    products: (products || []).map(p => ({
      id: p.id,
      name: p.name,
      slug: p.slug,
      is_active: p.is_active ?? true,
    })),
  });
}

// Get the default/platform brand (MindCo Health)
export async function getDefaultBrand(): Promise<ApiResult<Brand | null>> {
  const { data, error } = await supabase
    .from('brands')
    .select('*')
    .eq('slug', 'mindco')
    .maybeSingle();

  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }

  return ok(data ? castToBrand(data) : null);
}

// ============ Brand Mutations ============

export async function createBrand(input: CreateBrandInput): Promise<ApiResult<Brand>> {
  const { data, error } = await supabase
    .from('brands')
    .insert([input])
    .select()
    .single();

  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }

  return ok(castToBrand(data));
}

export async function updateBrand(id: string, input: UpdateBrandInput): Promise<ApiResult<Brand>> {
  const { data, error } = await supabase
    .from('brands')
    .update(input)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }

  return ok(castToBrand(data));
}

export async function deleteBrand(id: string): Promise<ApiResult<null>> {
  const { error } = await supabase
    .from('brands')
    .delete()
    .eq('id', id);

  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }

  return ok(null);
}

// ============ Brand Admin Queries ============

export async function getBrandAdmins(brandId: string): Promise<ApiResult<BrandAdmin[]>> {
  const { data, error } = await supabase
    .from('brand_admins')
    .select('*')
    .eq('brand_id', brandId)
    .order('created_at');

  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }

  return ok((data || []).map(castToBrandAdmin));
}

export async function getUserBrandRole(userId: string): Promise<ApiResult<BrandAdmin | null>> {
  const { data, error } = await supabase
    .from('brand_admins')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }

  return ok(data ? castToBrandAdmin(data) : null);
}

export async function getCurrentUserBrand(): Promise<ApiResult<Brand | null>> {
  // First get the user's brand admin record
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    return ok(null);
  }

  const { data: brandAdmin, error: adminError } = await supabase
    .from('brand_admins')
    .select('brand_id')
    .eq('user_id', user.id)
    .maybeSingle();

  if (adminError) {
    return err(ApiError.fromSupabaseError(adminError));
  }

  if (!brandAdmin) {
    return ok(null);
  }

  // Get the brand details
  return getBrandById(brandAdmin.brand_id);
}

// ============ Brand Admin Mutations ============

export async function addBrandAdmin(
  brandId: string,
  userId: string,
  role: AdminRole,
  productIds?: string[]
): Promise<ApiResult<BrandAdmin>> {
  const { data, error } = await supabase
    .from('brand_admins')
    .insert([{
      brand_id: brandId,
      user_id: userId,
      role,
      product_ids: productIds || [],
    }])
    .select()
    .single();

  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }

  return ok(castToBrandAdmin(data));
}

export async function updateBrandAdmin(
  id: string,
  role: AdminRole,
  productIds?: string[]
): Promise<ApiResult<BrandAdmin>> {
  const { data, error } = await supabase
    .from('brand_admins')
    .update({
      role,
      product_ids: productIds,
    })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }

  return ok(castToBrandAdmin(data));
}

export async function removeBrandAdmin(id: string): Promise<ApiResult<null>> {
  const { error } = await supabase
    .from('brand_admins')
    .delete()
    .eq('id', id);

  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }

  return ok(null);
}
